/**
 * Lobby game-catalog query hook.
 *
 * Replaces GameLobby's old serial `fetchGameCatalog` (getGames -> getGamesByCategory
 * -> getNewGames chained behind three `await`s). The audit flagged that chain as a
 * request waterfall. Here the three reads fire in parallel inside one queryFn via
 * Promise.allSettled, so the slowest single round-trip bounds the catalog load
 * instead of the sum of three.
 *
 * Returns a normalized view-model: the full per-category catalog object with slots
 * pre-sorted newest-first. The dedicated `new` and `slots` reads enrich the catalog
 * payload (matching what /games/new and /games/category/slots return), but a failure
 * in either degrades gracefully to the catalog's own arrays rather than failing the
 * whole query.
 *
 * See frontend/src/shared/data/README.md for the data-layer conventions.
 */

import { useQuery } from "@tanstack/react-query";
import casinoService from "../../../services/casinoService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { normalizeQueryError } from "../../../shared/data/errors";
import logger from "../../../shared/utils/logger";

const EMPTY_CATALOG = {
  popular: [],
  new: [],
  slots: [],
  live_casino: [],
  table_games: [],
  jackpots: [],
  instant: [],
  all: [],
};

// release_date is the field /games/category/slots actually returns; created_at /
// createdAt are defensive fallbacks for other catalog shapes.
const sortByNewest = (games) =>
  [...games].sort((a, b) => {
    const dateA = new Date(a.release_date || a.created_at || a.createdAt || 0);
    const dateB = new Date(b.release_date || b.created_at || b.createdAt || 0);
    return dateB - dateA;
  });

/**
 * @param {{ catalogLimit?: number, slotsLimit?: number, newLimit?: number }} [options]
 */
export function useGameCatalog(options = {}) {
  const { catalogLimit = 20, slotsLimit = 100, newLimit = 20 } = options;

  return useQuery({
    queryKey: queryKeys.games.lobby({ catalogLimit, slotsLimit, newLimit }),
    queryFn: async () => {
      // Parallel, not serial: the catalog, the dedicated slots list, and the
      // dedicated new-games list have no data dependency on each other.
      const [catalogRes, slotsRes, newRes] = await Promise.allSettled([
        casinoService.getGames({
          includePopular: true,
          includeNew: true,
          limit: catalogLimit,
        }),
        casinoService.getGamesByCategory("slots", { limit: slotsLimit }),
        casinoService.getNewGames({ limit: newLimit }),
      ]);

      // The base catalog is the only hard dependency. If it failed, surface the
      // error so the component shows the catalog skeleton/error, not empty rows.
      if (catalogRes.status === "rejected") {
        throw normalizeQueryError(
          catalogRes.reason,
          "Failed to load games. Please try again.",
        );
      }

      const catalog = catalogRes.value?.catalog;
      if (!catalog) {
        logger.warn("GameLobby: No catalog in response, using empty arrays");
        return EMPTY_CATALOG;
      }

      // Slots: prefer the dedicated category fetch (sorted newest-first), fall
      // back to the catalog's own slots on failure.
      let slots = catalog.slots || [];
      if (slotsRes.status === "fulfilled") {
        slots =
          slotsRes.value?.data?.games ||
          slotsRes.value?.games ||
          catalog.slots ||
          [];
      } else {
        logger.warn(
          "GameLobby: Failed to fetch all slots, using catalog slots",
          slotsRes.reason,
        );
      }

      // New: prefer the dedicated /games/new dataset (correct date fields), fall
      // back to catalog.new on failure or empty.
      let newGames = catalog.new || [];
      if (newRes.status === "fulfilled") {
        const dedicated = Array.isArray(newRes.value?.games)
          ? newRes.value.games
          : [];
        if (dedicated.length > 0) newGames = dedicated;
      } else {
        logger.warn(
          "GameLobby: fallback to catalog.new - failed fetching dedicated new games",
          newRes.reason,
        );
      }

      return {
        popular: catalog.popular || [],
        new: newGames,
        slots: sortByNewest(slots),
        live_casino: catalog.live_casino || [],
        table_games: catalog.table_games || [],
        instant: catalog.instant || [],
        jackpots: catalog.jackpots || [],
        all: catalog.all || [],
      };
    },
    placeholderData: (previous) => previous,
  });
}

export default useGameCatalog;
